import React, { useMemo, useState } from "react";
import TimeSpentByDayChart, {
    ALL_MODULES_SELECT_ID,
} from "./TimeSpentByDayChart/TimeSpentByDayChart";
import DatePagination from "./DatePagination/DatePagination";
import { ClassroomAnalytics } from "../../../../../interfaces/Dashboard";
import { useData } from "../../../../../hooks/useData";
import { WeekInfo, ModuleTimeSpent, DateRange } from "../Types";
import { defineMessages, useIntl } from "react-intl";
import { aggregateWeeksInfo, generateModulesTimeSpent } from "./utils";
import InfoTooltip from "../../../../../design-system-components/InfoTooltip/InfoTooltip";
import { ModulesListEntry } from "../StudentDetails";
import "./TimeSpentOnModules.scss";

interface Props {
    classroom: ClassroomAnalytics;
    studentId: string;
    currentModuleId: string;
    modulesList: ModulesListEntry[];
}

const TimeSpentOnModules = ({
    classroom,
    studentId,
    currentModuleId,
    modulesList,
}: Props) => {
    const data = useData();
    const intl = useIntl();

    const datesInfo = useMemo<WeekInfo[] | undefined>(() => {
        if (!classroom) return;
        return aggregateWeeksInfo(classroom, studentId, data, intl);
    }, [classroom, data, intl, studentId]);

    const [week, setWeek] = useState<DateRange | undefined>(
        datesInfo && datesInfo[0].range
    );

    const moduleTimeSpent = useMemo<ModuleTimeSpent[] | undefined>(() => {
        if (!datesInfo || !week) return;
        return generateModulesTimeSpent(week, datesInfo);
    }, [datesInfo, week]);

    const onWeekChange = (week: DateRange) => {
        setWeek(week);
    };

    return (
        <div className="time-spent-on-modules">
            <h3>
                {intl.formatMessage(messages.timeSpentOnModules)}{" "}
                <InfoTooltip
                    className="time-spent-on-modules__tooltip"
                    label={intl.formatMessage(messages.tooltip)}
                />
            </h3>
            <p className="time-spent-on-modules__subtitle">
                {intl.formatMessage(messages.subtitle)}
            </p>
            {datesInfo && moduleTimeSpent && (
                <>
                    <TimeSpentByDayChart
                        moduleTimeSpent={moduleTimeSpent}
                        currentModuleId={
                            modulesList.find((mod) => mod.id == currentModuleId)
                                ? currentModuleId
                                : ALL_MODULES_SELECT_ID
                        }
                        modulesList={modulesList}
                    />
                    <DatePagination
                        onWeekChange={onWeekChange}
                        weeksInfo={datesInfo}
                    />
                </>
            )}
        </div>
    );
};

export default TimeSpentOnModules;

const messages = defineMessages({
    timeSpentOnModules: {
        id: "student-details-timeSpentOnModules",
        defaultMessage: "Time spent on modules",
    },
    subtitle: {
        id: "student-details-timeSpentOnModules-subtitle",
        defaultMessage: "On AI module",
    },
    tooltip: {
        id: "student-details-timeSpentOnModules-tooltip",
        defaultMessage:
            "Training duration is the time spent performing the exercises. The time spent reading the exercise corrections is not counted here.",
    },
    noTimeSpentOnModules: {
        id: "student-details-noTimeSpentOnModules",
        defaultMessage:
            "The student has not worked on any module during this period.",
    },
});
